<template>
  <div class="patient-history-page">
    <div class="row patient-history-layout">

      <!-- Patient Data -->
      <div class="col-12 patient-history-sidebar">
        <Card v-bind:title="'Pasien'" card-class="patient-history-card">
          <template v-slot:body>
            <div class="patient-history-summary">
              <div class="patient-history-profile">
                <div class="symbol symbol-60 symbol-xxl-100 mr-5 align-self-center">
                  <div style='height: 100px; width: 100px; position: relative'>
                    <img
                      class="image"
                      :src="patient.photo"
                    >
                  </div>
                  <i class="symbol-badge bg-success"></i>
                </div>
                <div class="patient-history-profile__identity">
                  <a class="font-weight-bolder font-size-h5 text-dark-75 text-hover-primary">{{ patient.name }}</a>
                  <div class="text-muted">Gol. Darah: {{ patient.blood_type }}</div>
                  <div class="mt-2">
                    <a
                      class="btn btn-sm btn-success font-weight-bold mr-2 py-2 px-3 px-xxl-5 my-1"
                      @click="$router.push({ path: '/patient/detail/' + patient.id })"
                    >Detail</a>
                  </div>
                </div>
              </div>
              <div class="patient-history-demographics">
                <div>
                  <span class="font-weight-bold">Jenis Kelamin</span>
                  <span class="text-muted text-hover-primary">{{ patient.gender }}</span>
                </div>
                <div>
                  <span class="font-weight-bold">Tanggal Lahir</span>
                  <span class="text-muted text-hover-primary">{{ patient.display_birt_date }}</span>
                </div>
                <div>
                  <span class="font-weight-bold">Email</span>
                  <span class="text-muted text-hover-primary">{{ patient.email }}</span>
                </div>
                <div>
                  <span class="font-weight-bold">Handphone</span>
                  <span class="text-muted text-hover-primary">{{ patient.mobile_phone }}</span>
                </div>
                <div>
                  <span class="font-weight-bold">Alamat</span>
                  <span class="text-muted text-hover-primary">{{ patient.address }}</span>
                </div>
              </div>
            </div>
          </template>
        </Card>
      </div>

      <!-- History Section -->
      <div class="col-12 patient-history-main">
        <Card v-bind:title="`Riwayat ${typeof patient.name !== 'undefined' ? patient.name : ''}`" card-class="patient-history-card patient-history-card--main">
          <template v-slot:body>
            <div v-if="dataLoaded == true">
              <b-tabs class="mt-n3 patient-history-tabs">

                <!-- Perjanjian -->
                <b-tab
                  title="Perjanjian"
                  active
                >
                  <AppointmentHistory />
                </b-tab>

                <!-- Riwayat -->
                <b-tab title="Rekam Medis">
                  <TreatmentHistory />
                </b-tab>

                <!-- Resep -->
                <b-tab title="Resep">
                  <PrescriptionHistory />
                </b-tab>

                <!-- Laboratorium -->
                <b-tab title="Lab">
                  <LaboratoriumHistory />
                </b-tab>

                <!-- Dokumen -->
                <b-tab title="Dokumen">
                  <DocumentHistory
                    purpose="history"
                    :dataId="patient.id"
                  />
                </b-tab>

                <!-- Bed -->
                <b-tab title="Bed">
                  <BedHistory :bedData="patient.id" />
                </b-tab>

                <!-- Waktu -->
                <b-tab title="Waktu">
                  <TimelineHistory />
                </b-tab>

                <b-tab
                  v-for="group in specialistGroups"
                  :key="`specialist-${group.code}`"
                  :title="group.label"
                >
                  <SpecialistHistoryPanel :group="group" />
                </b-tab>

              </b-tabs>
            </div>
          </template>
        </Card>
      </div>

    </div>
  </div>
</template>

<script>

import Card from "@/view/content/Card.vue"
import AppointmentHistory from '@/component/patients/AppointmentHistory.vue'
import TreatmentHistory from '@/component/patients/TreatmentHistory.vue'
import PrescriptionHistory from '@/component/patients/PrescriptionHistory.vue'
import LaboratoriumHistory from '@/component/patients/LaboratoriumHistory.vue'
// import DocumentHistory from '@/component/patients/DocumentList.vue'
import DocumentHistory from '@/view/pages/patients/fileCard.vue'
import BedHistory from '@/component/beds/Table.vue'
import TimelineHistory from '@/component/patients/TimelineHistory.vue'
import SpecialistHistoryPanel from '@/component/patients/SpecialistHistoryPanel.vue'
import ApiService from '@/core/services/api.service'
import module from '@/core/modules/CrudModule.js'
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module"

export default {

  name: 'History',

  components: {
    Card,
    AppointmentHistory,
    TreatmentHistory,
    PrescriptionHistory,
    LaboratoriumHistory,
    DocumentHistory,
    BedHistory,
    TimelineHistory,
    SpecialistHistoryPanel
  },

  data() {
    return {
      // Patient Data
      patient: {},
      dataLoaded: false,
      specialistGroups: []
    }
  },

  methods: {

    async getPatient() {
      this.patient = await module.get('patients/' + this.$route.params.id)
      if (this.patient == null) {
        this.$router.push('/patient/list')
      } else {
        this.dataLoaded = true
        this.loadSpecialistHistory()
      }
    },

    responseItems(response) {
      const payload = response && response.data ? response.data : {}
      if (Array.isArray(payload.data)) return payload.data
      if (payload.data && Array.isArray(payload.data.data)) return payload.data.data
      return []
    },

    async optionalRequest(path) {
      try {
        return await ApiService.query(path, {
          params: { patient_id: this.patient.id, page_size: 100 }
        })
      } catch (error) {
        // A specialist module can be disabled or unavailable to the current role.
        // In that case its history tab must stay hidden.
        return null
      }
    },

    async loadSpecialistHistory() {
      const patientId = this.patient.id
      if (!patientId) return

      const [registryResponse, verticalResponse, odontogramResponse, periodontalResponse, planResponse, therapyResponse] = await Promise.all([
        this.optionalRequest('vertical-clinical/registry'),
        this.optionalRequest('vertical-clinical/records'),
        this.optionalRequest('dental/odontograms'),
        this.optionalRequest('dental/periodontal-charts'),
        this.optionalRequest('dental/treatment-plans'),
        this.optionalRequest('therapy/cases')
      ])

      // Ignore an older request if the user navigated to another patient meanwhile.
      if (Number(this.patient.id) !== Number(patientId)) return

      const registry = registryResponse && registryResponse.data
        ? (registryResponse.data.data || {})
        : {}
      const verticalLabels = {
        aesthetic: 'Klinik Kecantikan',
        maternal_child: 'Klinik Ibu & Anak',
        mcu: 'Medical Check Up',
        eye: 'Klinik Mata',
        physiotherapy: 'Fisioterapi',
        occupational: 'Kesehatan Kerja',
        vaccination: 'Vaksinasi'
      }
      const verticalGroups = {}

      this.responseItems(verticalResponse).forEach(record => {
        const code = record.vertical_code || (registry[record.record_type] && registry[record.record_type].vertical)
        if (!code) return
        if (!verticalGroups[code]) {
          verticalGroups[code] = {
            code,
            label: verticalLabels[code] || code.replace(/_/g, ' ').replace(/\b\w/g, character => character.toUpperCase()),
            icon: this.specialistIcon(code),
            items: []
          }
        }
        const definition = registry[record.record_type] || {}
        verticalGroups[code].items.push({
          id: `vertical-${record.id}`,
          title: definition.label || record.record_type,
          reference: record.record_number,
          date: record.observed_at || record.updated_at,
          status: record.status,
          summary: record.clinical_summary,
          meta: record.version ? `Versi ${record.version}` : '',
          route: {
            path: `/vertical-clinical/${record.record_type}`,
            query: { patient_id: patientId, record_id: record.id }
          }
        })
      })

      const groups = Object.keys(verticalGroups).map(code => verticalGroups[code])
      const dentalItems = []
      this.responseItems(odontogramResponse).forEach(item => dentalItems.push({
        id: `odontogram-${item.id}`,
        title: 'Odontogram',
        reference: item.encounter_number || `Versi ${item.version}`,
        date: item.charted_at || item.updated_at,
        status: item.status,
        summary: item.clinical_notes,
        meta: item.dentition ? `Dentisi ${item.dentition}` : '',
        route: { path: '/dental', query: { tab: 'odontogram', patient_id: patientId, odontogram_id: item.id } }
      }))
      this.responseItems(periodontalResponse).forEach(item => dentalItems.push({
        id: `periodontal-${item.id}`,
        title: 'Pemeriksaan Periodontal',
        reference: `Versi ${item.version}`,
        date: item.charted_at || item.updated_at,
        status: item.status,
        summary: item.notes,
        route: { path: '/dental', query: { tab: 'periodontal', patient_id: patientId, periodontal_id: item.id } }
      }))
      this.responseItems(planResponse).forEach(item => dentalItems.push({
        id: `dental-plan-${item.id}`,
        title: 'Rencana Perawatan Gigi',
        reference: item.plan_number,
        date: item.accepted_at || item.updated_at,
        status: item.status,
        summary: item.clinical_goal || item.notes,
        meta: item.total ? `Nilai rencana Rp ${Number(item.total).toLocaleString('id-ID')}` : '',
        route: { path: '/dental', query: { tab: 'treatment', patient_id: patientId, plan_id: item.id } }
      }))
      if (dentalItems.length) {
        groups.push({ code: 'dental', label: 'Klinik Gigi', icon: 'fas fa-tooth', items: dentalItems })
      }

      const therapyItems = this.responseItems(therapyResponse).map(item => ({
        id: `therapy-${item.id}`,
        title: 'Episode Terapi',
        reference: item.case_number,
        date: item.started_at || item.updated_at,
        status: item.status,
        summary: item.physio_diagnosis || item.medical_diagnosis,
        meta: `${item.completed_sessions || 0} dari ${item.authorized_sessions || 0} sesi selesai`,
        route: { path: '/therapy', query: { case_id: item.id } }
      }))
      if (therapyItems.length) {
        groups.push({ code: 'therapy', label: 'Klinik Therapy', icon: 'fas fa-walking', items: therapyItems })
      }

      groups.forEach(group => {
        group.items.sort((left, right) => new Date(right.date || 0) - new Date(left.date || 0))
      })
      this.specialistGroups = groups
    },

    specialistIcon(code) {
      return {
        aesthetic: 'fas fa-spa',
        maternal_child: 'fas fa-baby',
        mcu: 'fas fa-notes-medical',
        eye: 'fas fa-eye',
        physiotherapy: 'fas fa-walking',
        occupational: 'fas fa-hard-hat',
        vaccination: 'fas fa-syringe'
      }[code] || 'fas fa-stethoscope'
    }

  },

  mounted() {
    // Breadcrumb
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: "Pasien", route: "" },
      { title: "Daftar Pasien", route: "/patient/list" },
      { title: "Riwayat" },
    ]);
    // Get Patient
    this.getPatient()
  },

}
</script>

<style>
.patient-history-layout > [class*="col-"] {
  min-width: 0;
}
.patient-history-card,
.patient-history-card .card-body,
.patient-history-main,
.patient-history-tabs,
.patient-history-tabs .tab-content,
.patient-history-tabs .tab-pane {
  min-width: 0 !important;
  max-width: 100% !important;
}
.patient-history-card--main {
  overflow: hidden;
}
.patient-history-tabs {
  display: block !important;
  width: 100% !important;
  overflow: hidden !important;
  padding: 0 !important;
  background: transparent !important;
}
.patient-history-tabs > .nav-tabs {
  display: flex !important;
  width: 100% !important;
  max-width: 100% !important;
  overflow-x: auto !important;
  overflow-y: hidden !important;
  flex-wrap: nowrap !important;
}
.patient-history-tabs > .nav-tabs .nav-link {
  white-space: nowrap;
}
.patient-history-tabs .tab-content {
  overflow-x: auto;
}
.patient-history-profile {
  display: flex;
  align-items: center;
  min-width: 0;
}
.patient-history-summary {
  display: grid;
  grid-template-columns: minmax(260px, 340px) minmax(0, 1fr);
  align-items: center;
  gap: 2rem;
}
.patient-history-profile__identity {
  min-width: 0;
}
.patient-history-profile__identity > a {
  display: inline-block;
  color: #203b55 !important;
  font-size: 1.2rem !important;
  font-weight: 800 !important;
  line-height: 1.25;
}
.patient-history-profile__identity > .text-muted {
  margin-top: .3rem;
  color: #6b7f91 !important;
  font-size: .84rem;
  font-weight: 500;
}
.patient-history-profile__identity > a,
.patient-history-demographics .text-muted {
  overflow-wrap: anywhere;
}
.patient-history-demographics {
  display: grid;
  grid-template-columns: repeat(5, minmax(130px, 1fr));
  gap: 1rem;
}
.patient-history-demographics > div {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: .35rem;
  padding-left: 1rem;
  border-left: 1px solid #e4ebf1;
}
.patient-history-demographics .font-weight-bold {
  color: #526b80;
  font-size: .68rem;
  font-weight: 800 !important;
  letter-spacing: .055em;
  line-height: 1.2;
  text-transform: uppercase;
}
.patient-history-demographics .text-muted {
  color: #263f56 !important;
  font-size: .9rem;
  font-weight: 600;
  line-height: 1.45;
}
@media (max-width: 1199.98px) {
  .patient-history-demographics {
    grid-template-columns: repeat(3, minmax(150px, 1fr));
  }
}
@media (max-width: 767.98px) {
  .patient-history-summary {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
  .patient-history-demographics {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
@media (max-width: 479.98px) {
  .patient-history-demographics {
    grid-template-columns: 1fr;
  }
}
</style>
